import { useMemo } from 'react';
import { Link } from '@/components/Link';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { serializeJsonLd } from '../lib/jsonLd';
import type { ApiDetail } from '../features/catalog/model';
import { useCatalogDetail } from '../features/catalog/queries';
import { benchmarksQueryOptions } from '../features/usage/queries';
import { pageBenchmarksQueryOptions, pageNetworkUsageQueryOptions } from '../features/usage/pageQueries';
import { selectDisplayBenchmarks } from '../lib/benchmarks';
import { toSkuUsage } from '../lib/networkUsage';
import { endpointOrder } from '../lib/catalog';
import { ccVar } from '../lib/css';
import { apiSchemaGraph } from '../lib/seo/apiSchema';
import { ApiFaq } from './api-detail/ApiFaq';
import { ApiHeader } from './api-detail/ApiHeader';
import { BenchmarkPanel } from './api-detail/BenchmarkPanel';
import { EndpointTable } from './api-detail/EndpointTable';
import { PlatformTools } from './api-detail/PlatformTools';
import { useCatalogLinks } from '../components/CatalogLinks';
import { DataRequestBox } from '../features/feedback/DataRequestBox';
import { Band } from '../components/editorial/Band';
import { FunnelBanner } from '../components/editorial/FunnelBanner';
import { CatalogFunnelAction } from '../features/catalog/browse/CatalogFunnelAction';
import { DashboardHeader } from '../components/DashboardHeader';
import { DashboardApiDetailBody } from './api-detail/DashboardApiDetailBody';
import { ParaglideMessage } from '@inlang/paraglide-js-react';
import { m } from '../paraglide/messages';

/** Anchor the header's "Full breakdown" link jumps to. */
const PANEL_ID = 'measured-performance';

interface Props {
  apiId: string;
  /** Resolved by the route loader (SSR), so the header + endpoint list render
   *  server-side and the page is crawlable. Null = unknown platform. */
  detail: ApiDetail | null;
  icon: string;
  /** Which composition to render. Chosen by the route that owns the URL, never
   *  sniffed: `site` is the editorial page Google indexes, `dashboard` is the
   *  working page a signed-in customer browses. */
  presentation: 'site' | 'dashboard';
}

/** API detail page (/api/:apiId): header, measured cards, and the endpoint table.
 *
 *  Everything here is server-rendered from the route loader, measured figures
 *  included. They are not decorative: the uptime, latency, and request volume
 *  behind each endpoint are the only data on this site a competitor cannot
 *  restate, so they have to be in the HTML. These `useQuery` calls read the
 *  loader's cache on the server and refresh it in the browser. */
export const ApiDetailPage = ({
  apiId,
  detail: initialDetail,
  icon,
  presentation,
}: Props) => {
  const links = useCatalogLinks();
  const queryClient = useQueryClient();
  const detail = useCatalogDetail(apiId, initialDetail, presentation === 'site' ? 'platform' : 'all');
  const scope = { platform: apiId };
  const usageQuery = useQuery({
    ...pageNetworkUsageQueryOptions(queryClient, presentation === 'site' ? scope : undefined),
    select: toSkuUsage,
  });
  const benchmarkQuery = useQuery({
    ...(presentation === 'site'
      ? pageBenchmarksQueryOptions(queryClient, scope)
      : benchmarksQueryOptions()),
    select: selectDisplayBenchmarks,
  });
  const usage = usageQuery.data ?? {};
  const benchmarks = benchmarkQuery.data ?? {};

  const endpoints = useMemo(
    () => (detail ? endpointOrder(detail, usage) : []),
    [detail, usage],
  );

  if (!detail) {
    if (presentation === 'dashboard') {
      return (
        <DashboardHeader
          crumb={<Link to={links.catalog}>{m.platform_catalog()}</Link>}
          title={m.platform_not_found_title()}
          description={
            <ParaglideMessage
              message={m.platform_not_found_dashboard}
              inputs={{ apiId }}
              markup={{
                link: ({ children }) => (
                  <Link to={links.catalog} className="text-accent">
                    {children}
                  </Link>
                ),
              }}
            />
          }
        />
      );
    }
    return (
      <Band>
        <div className="band">
          <h1 className="display-2 text-text">{m.platform_not_found_title()}</h1>
          <p className="lede mt-5">
            <ParaglideMessage
              message={m.platform_not_found_site}
              inputs={{ apiId }}
              markup={{
                link: ({ children }) => (
                  <Link to={links.catalog} className="link-dotted">
                    {children}
                  </Link>
                ),
              }}
            />
          </p>
        </div>
      </Band>
    );
  }

  if (presentation === 'dashboard') {
    return <DashboardApiDetailBody detail={detail} icon={icon} endpoints={endpoints} />;
  }

  const single = endpoints.every((e) => e.providers.length === 1);
  const funnelHeading = (
    <ParaglideMessage
      message={m.platform_funnel_heading}
      inputs={{ name: detail.name }}
      markup={{
        br: () => <br />,
        muted: ({ children }) => <span className="muted-word">{children}</span>,
      }}
    />
  );

  return (
    <div className="rhythm-tight" style={ccVar(detail.color)}>
      <script
        type="application/ld+json"
        // Service + BreadcrumbList for rich results / AI citation.
        dangerouslySetInnerHTML={{ __html: serializeJsonLd(apiSchemaGraph(detail, apiId)) }}
      />
      <ApiHeader
        api={detail}
        icon={icon}
        usage={usage}
        benchmarks={benchmarks}
        panelHref={`#${PANEL_ID}`}
      />
      <Band>
        <div className="band rule-dashed">
          <EndpointTable
            apiId={detail.id}
            endpoints={endpoints}
            color={detail.color}
            usage={usage}
            benchmarks={benchmarks}
          />
        </div>

        {/* Everything the header no longer has room for: how the prices in the
            table are picked, and the ask from a reader who did not find their
            data in it. */}
        <div className="band-prose flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between sm:gap-8">
          <div className="max-w-[62ch]">
            <h2 className="display-3 text-text">{m.platform_about_heading({ name: detail.name })}</h2>
            <p className="lede mt-5">
              {single ? (
                m.platform_about_single()
              ) : (
                <ParaglideMessage
                  message={m.platform_about_multi}
                  markup={{ b: ({ children }) => <strong>{children}</strong> }}
                />
              )}
            </p>
          </div>
          <DataRequestBox
            className="shrink-0"
            label={m.platform_request_data_label()}
            placeholder={m.platform_request_data_placeholder({ name: detail.name })}
          />
        </div>
      </Band>

      <div id={PANEL_ID} className="scroll-mt-20">
        <BenchmarkPanel api={detail} benchmarks={benchmarks} />
      </div>
      <PlatformTools apiId={detail.id} platform={detail.name} />
      <ApiFaq api={detail} benchmarks={benchmarks} />
      <FunnelBanner
        heading={funnelHeading}
        action={<CatalogFunnelAction />}
      />
    </div>
  );
};
